import { connect } from 'react-redux';
import { css } from 'styled-components';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import overrideStyle from 'lib/overrideStyle';

import CSSTransitionOnMount from 'presentation/components/lib/CSSTransitionOnMount';
import { CustomTextContext } from 'context/CustomTextContext';
import { getIsNavigatingBack, getResultsScrollTop } from 'app/modules';
import { useDebounced } from 'presentation/hooks/debounce';
import SearchResultsItem from 'presentation/selfService/SearchResultsItem';
import selfServiceEvents from 'events/selfService';

export function SearchResults({
  focusIndex,
  initialScrollTop,
  isNavigatingBack,
  onBlur,
  onSetFocused,
  onScroll,
  search,
}) {
  const visibleResults = useCachedResults(search);
  const { noSearchResults } = useContext(CustomTextContext);
  const resultsRef = useRef(null);
  useSetScrollTop(resultsRef, initialScrollTop, visibleResults);
  const handleScroll = useHandleScroll(resultsRef, onScroll);
  const scrollIntoView = useCallback(bounds => {
    const resultsBounds = resultsRef.current.getBoundingClientRect();

    if (bounds.top < resultsBounds.top) {
      resultsRef.current.scrollTop = resultsRef.current.scrollTop - (resultsBounds.top - bounds.top);
    } else if (bounds.bottom > resultsBounds.bottom) {
      resultsRef.current.scrollTop = resultsRef.current.scrollTop + (bounds.bottom - resultsBounds.bottom);
    }
  }, []);

  if (visibleResults.length === 0) {
    return (
      <Results data-aid="selfService-noSearchResults" ref={resultsRef}>
        <ZeroState>{noSearchResults}</ZeroState>
      </Results>
    );
  }

  return (
    <ResultsWrapper className={search.isLoading ? 'resultsWrapper-isLoading' : undefined}>
      <CSSTransitionOnMount className="gladlySelfService-fadeResultsIn" timeout={300}>
        <ResultsAnimator isNavigatingBack={isNavigatingBack} onScroll={handleScroll} ref={resultsRef}>
          <Results data-aid="selfService-searchResults">
            {visibleResults.map((answer, index) => (
              <SearchResultsItem
                answerId={answer.id}
                isFocused={index === focusIndex}
                index={index}
                isSearchedAnswer={true}
                key={answer.id}
                name={answer.name && answer.name.toString()}
                onBlur={onBlur}
                onSetFocused={onSetFocused}
                scrollIntoView={scrollIntoView}
              />
            ))}
          </Results>
        </ResultsAnimator>
      </CSSTransitionOnMount>
    </ResultsWrapper>
  );
}

// When we get loaded results back, we want to preserve them so that we can continue to render them
// when issuing the next search.
function useCachedResults(search) {
  const previousResultsRef = useRef(null);
  useEffect(() => {
    if (!search.isLoading) {
      previousResultsRef.current = search.results;
    }
  }, [search]);
  return (search.isLoading ? previousResultsRef.current : search.results) || [];
}

function useHandleScroll(resultsRef, onScroll) {
  const handleScroll = useCallback(() => {
    resultsRef.current && onScroll(resultsRef.current.scrollTop);
  });
  return useDebounced(handleScroll, 100);
}

function useSetScrollTop(resultsRef, initialScrollTop, visibleResults) {
  // Whenever visible results change, we want to be scrolled back to the top
  useEffect(() => {
    resultsRef.current.scrollTop = 0;
  }, [visibleResults]);
  // But when we first mount, use the saved scroll top instead
  useEffect(() => {
    resultsRef.current.scrollTop = initialScrollTop;
  }, []);
}

const ResultsWrapper = overrideStyle('div')`
  display: flex;
  flex: 1;
  position: relative;

  &.resultsWrapper-isLoading {
    opacity: 0.5;
    transition: opacity 1000ms ease 500ms;
  }

  ${p => p.isLoading && fadedResults};
`;
const fadedResults = css`
  opacity: 0.5 !important;
`;

const ResultsAnimator = overrideStyle('span')`
  flex: 1;
  opacity: 0;
  overflow: auto;
  transform: ${({ isNavigatingBack }) => (isNavigatingBack ? 'translateX(-40px)' : 'translateY(-40px)')};

  &.gladlySelfService-fadeResultsIn-enter-active {
    pointer-events: none;
    opacity: 1;
    transition: opacity 300ms cubic-bezier(0.42, 0, 1, 1), transform 300ms cubic-bezier(0, 0, 0.58, 1);
    transform: translate(0px, 0px);
  }
  &.gladlySelfService-fadeResultsIn-enter-done {
    opacity: 1;
    transform: translate(0px, 0px);
  }
`;

const Results = overrideStyle('div')`
  opacity: ${p => (p.isLoading ? '0.5' : '1')};
  transition: opacity 300ms ease;
  padding: 5px 12px;
`;

const ZeroState = overrideStyle('div')`
  font-family: ${p => p.theme.fontLibrary.primary};
  font-size: 14px;
  opacity: ${p => (p.isLoading ? '0.5' : '1')};
  transition: opacity 300ms ease;
  padding: 25px 30px;
`;

SearchResults.propTypes = {
  currentFocusIndex: PropTypes.number.isRequired,
  isLoading: PropTypes.bool,
  isNavigatingBack: PropTypes.bool,
  onBlur: PropTypes.func.isRequired,
  onSetFocused: PropTypes.func.isRequired,
  results: PropTypes.array.isRequired,
  shouldShowDropDown: PropTypes.bool,
  value: PropTypes.string.isRequired,
};

function mapStateToProps(state, props) {
  return {
    ...props,
    initialScrollTop: getResultsScrollTop(state),
    isNavigatingBack: getIsNavigatingBack(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onScroll: scrollTop => {
      dispatch(selfServiceEvents.updateResultsScrollTop(scrollTop));
    },
  };
}

const SearchResultsContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(SearchResults);

export default SearchResultsContainer;
